import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';

/**
 * V2 API Service - Complete implementation of all v2 endpoints
 */

// Helper function to get auth headers
const getAuthHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

/**
 * Step 1: Filter Stops
 * Apply filters to get stops (unique customer+address combinations)
 * @param {Object} filters - Filter criteria
 * @param {string} token - JWT authentication token
 * @returns {Promise<{stops: Array, total_stops: number, filters_applied: Object}>}
 */
export const filterStopsV2 = async (filters, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/stops/filter`,
    { filters },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * Step 2: Generate Clusters
 * Cluster the filtered stops geographically
 * @param {Array} stopIds - Array of stop IDs from filter-stops
 * @param {Object} clusteringParams - Clustering parameters
 * @param {string} token - JWT authentication token
 * @returns {Promise<{clusters: Array, total_clusters: number}>}
 */
export const generateClustersV2 = async (stopIds, clusteringParams, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/clusters/generate`,
    {
      stop_ids: stopIds,
      clustering_params: clusteringParams
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * Step 3: Generate Itinerary
 * Generate AI-optimized itinerary for a cluster
 * @param {Object} cluster - Complete cluster object
 * @param {Object} tripDetails - Trip configuration
 * @param {string} token - JWT authentication token
 * @returns {Promise<{itinerary: Object, generation_time: number}>}
 */
export const generateItineraryV2 = async (cluster, tripDetails, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/itineraries/generate`,
    {
      cluster: cluster,
      trip_details: tripDetails
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * Step 4: Save Itinerary
 * Save the generated itinerary to database
 * @param {Object} itineraryData - Itinerary data from generate-itinerary
 * @param {Array} originalStops - Original stops from cluster
 * @param {string} token - JWT authentication token
 * @returns {Promise<{schedule_id: string, message: string, stops_saved: number}>}
 */
export const saveItineraryV2 = async (itineraryData, originalStops, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/itineraries/save`,
    {
      itinerary_data: itineraryData,
      original_stops: originalStops
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * Get all saved schedules
 * @param {string} token - JWT authentication token
 * @returns {Promise<Array>} - Array of saved schedules
 */
export const getSchedulesV2 = async (token) => {
  const response = await axios.get(
    `${API_BASE_URL}/schedules`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * Get specific schedule by ID
 * @param {string} scheduleId - Schedule UUID
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - Schedule details
 */
export const getScheduleByIdV2 = async (scheduleId, token) => {
  const response = await axios.get(
    `${API_BASE_URL}/schedules/${scheduleId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * Batch generate and save multiple itineraries in parallel
 * @param {Array} clusters - Array of cluster objects
 * @param {Object} tripDetails - Trip configuration
 * @param {Object} filters - Original filter params
 * @param {string} token - JWT authentication token
 * @returns {Promise<Array>} - Array of saved schedules
 */
export const batchGenerateItinerariesV2 = async (clusters, tripDetails, filters, token) => {
  const payload = {
    clusters: clusters,
    start_date: tripDetails.start_date,
    end_date: tripDetails.end_date,
    filter_params: filters,
  };

  const response = await axios.post(
    `${API_BASE_URL}/api/v2/batch/generate-and-save-itineraries`,
    payload,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * Complete v2 workflow - Filter, Cluster, Generate, and Save
 * This is the main function that orchestrates the entire v2 workflow
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Clustering parameters
 * @param {Object} tripDetails - Trip configuration
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Array>} - Array of saved schedules
 */
export const completeV2Workflow = async (filters, clusteringParams, tripDetails, token, onProgress) => {
  const errors = [];
  const results = [];
  
  try {
    // Step 1: Filter stops
    if (onProgress) onProgress({ step: 'filter', message: 'Finding customers...' });
    const filterResponse = await filterStopsV2(filters, token);
    
    if (!filterResponse.stops || filterResponse.stops.length === 0) {
      throw new Error('No customers found matching your filters');
    }
    
    if (onProgress) {
      onProgress({ 
        step: 'filter', 
        message: `Found ${filterResponse.total_stops} customers` 
      });
    }
    
    // Extract stop IDs
    const stopIds = filterResponse.stops.map(stop => stop.stop_id);
    
    // Step 2: Generate clusters
    if (onProgress) onProgress({ step: 'cluster', message: 'Grouping customers...' });
    const clusterResponse = await generateClustersV2(stopIds, clusteringParams, token);
    
    if (!clusterResponse.clusters || clusterResponse.clusters.length === 0) {
      throw new Error('No clusters could be formed with the current parameters');
    }
    
    if (onProgress) {
      onProgress({ 
        step: 'cluster', 
        message: `Created ${clusterResponse.total_clusters} clusters` 
      });
    }
    
    // Step 3 & 4: Generate and save itineraries for each cluster
    const savedSchedules = [];
    
    // Use batch processing if multiple clusters
    if (clusterResponse.clusters.length > 1) {
      if (onProgress) {
        onProgress({
          step: 'generate',
          message: `Generating and saving ${clusterResponse.clusters.length} itineraries in parallel...`
        });
      }

      try {
        // Use the single-shot batch endpoint that generates and saves
        const batchResponse = await batchGenerateItinerariesV2(
          clusterResponse.clusters,
          tripDetails,
          filters,
          token
        );

        // The backend returns the saved schedules directly
        if (batchResponse && Array.isArray(batchResponse.schedules)) {
          return batchResponse.schedules;
        }
        
        // If the structure is different, we might need to adapt, e.g., return batchResponse directly
        if (Array.isArray(batchResponse)) {
          return batchResponse;
        }

        console.warn("Batch processing completed but returned an unexpected format:", batchResponse);
        return [];

      } catch (error) {
        errors.push(`Batch generation and save failed: ${error.message}`);
        console.error('Batch generation and save failed:', error);
        // Fallback or re-throw, for now we will let the main catch handle it.
        throw error;
      }
    } else {
      // Single cluster - process normally
      const cluster = clusterResponse.clusters[0];
      
      if (onProgress) {
        onProgress({ step: 'generate', message: 'Generating itinerary...' });
      }
      
      const itineraryResponse = await generateItineraryV2(cluster, tripDetails, token);
      
      if (onProgress) {
        onProgress({ step: 'save', message: 'Saving itinerary...' });
      }
      
      const saveResponse = await saveItineraryV2(
        itineraryResponse.itinerary,
        cluster.stops,
        token
      );
      
      const schedule = {
        cluster: saveResponse.schedule_id,
        priority: cluster.priority,
        metadata: {
          itinerary_name: itineraryResponse.itinerary.name,
          region: cluster.stops[0]?.city || 'Unknown'
        },
        itinerary: {
          schedule: itineraryResponse.itinerary.days.flatMap(day => 
            day.stops.map((stop, index) => ({
              ...stop,
              day_number: day.day,
              stop_order: index + 1,
              type: 'customer'
            }))
          ),
          customers: cluster.stops
        },
        assigned_to: null,
        status: 'pending',
        created_at: new Date().toISOString(),
        region: cluster.stops[0]?.city || 'Unknown'
      };
      
      savedSchedules.push(schedule);
    }
    
    if (errors.length > 0) {
    }
    
    if (onProgress) {
      onProgress({ 
        step: 'complete', 
        message: `Successfully created ${savedSchedules.length} schedules!` 
      });
    }
    
    return savedSchedules;
    
  } catch (error) {
    console.error('V2 workflow failed:', error);
    if (error.isAxiosError) {
      console.error('Axios error details:', {
        message: error.message,
        url: error.config.url,
        method: error.config.method,
        data: JSON.parse(error.config.data), // Parse data to see the payload
        response: error.response?.data
      });
    }
    throw error;
  }
};

/**
 * Complete v2 workflow with retry logic
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Initial clustering parameters
 * @param {Object} tripDetails - Trip configuration
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Array>} - Array of saved schedules
 */
export const completeV2WorkflowWithRetry = async (filters, clusteringParams, tripDetails, token, onProgress) => {
  const originalStopsPerDay = clusteringParams.stops_per_day || 5;
  const fallbackValues = [4, 3, 2];
  
  // Try with original stops_per_day first
  try {
    const result = await completeV2Workflow(filters, clusteringParams, tripDetails, token, onProgress);
    
    if (result.length > 0) {
      return result;
    }
  } catch (error) {
    console.error(`V2 workflow failed with stops_per_day ${originalStopsPerDay}:`, error);
  }
  
  // Try with progressively smaller stops_per_day values
  for (const fallbackStopsPerDay of fallbackValues) {
    if (fallbackStopsPerDay >= originalStopsPerDay) continue;
    
    try {
      const fallbackParams = { ...clusteringParams, stops_per_day: fallbackStopsPerDay };
      const result = await completeV2Workflow(filters, fallbackParams, tripDetails, token, onProgress);
      
      if (result.length > 0) {
        return result;
      }
    } catch (error) {
      console.error(`V2 workflow failed with stops_per_day ${fallbackStopsPerDay}:`, error);
    }
  }
  
  throw new Error('Failed to generate schedules with all attempted parameters');
};

/**
 * Get enhanced customer details including quotes, orders, rentals, metrics, and company profile
 * @param {string} customerCode - Unique customer identifier
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - Customer details with opportunities and metrics
 */
export const getCustomerDetails = async (customerCode, token) => {
  const response = await axios.get(
    `${API_BASE_URL}/customers/${customerCode}/details`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * Get all opportunities for a specific customer
 * @param {string} customerCode - Unique customer identifier
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - Customer opportunities (quotes, orders, rentals)
 */
export const getCustomerOpportunities = async (customerCode, token) => {
  const response = await axios.get(
    `${API_BASE_URL}/customers/${customerCode}/opportunities`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Export all functions for flexibility
export default {
  filterStopsV2,
  generateClustersV2,
  generateItineraryV2,
  saveItineraryV2,
  getSchedulesV2,
  getScheduleByIdV2,
  batchGenerateItinerariesV2,
  completeV2Workflow,
  completeV2WorkflowWithRetry,
  getCustomerDetails
};